import { FieldErrors, FieldValues, Path } from "react-hook-form";

import { LanguageMappingInterface, LanguageObjectType } from "@components/forms/interfaces";
import { LANGUAGE_KEYS } from "@internationalization/index";

const objectIsLanguageObjectType = (o: object) => "es" in o && "en" in o && Object.keys(o).length === 2;
const objectIsErrorObjectType = (o: object) =>
  "message" in o && "ref" in o && "type" in o && Object.keys(o).length === 3;

/**
 * This function does a lot, its fancy, and it is integral to making our internationalization pattern
 * for form labels work without a hitch
 *
 * It takes an incoming formKey form React Hook Form (which is typed as per our mutation parameters)
 * and it starts reducing into our language mapping by stepping into each of the sections of the formKey
 * and using them as keys to walk down into our mapping file.
 *
 * Once we have reached the end, we set the error message accordingly based on our language passed in
 * @param formKey (ie: generalDetails.birthDay, guardianDetails.guardians.0.firstName, livingConditions.individualsInHouse.2.relationship)
 * @param mapping
 * @param chosenLanguage
 * @returns
 */
export function getLabelFromFormKey<T extends FieldValues>(
  formKey: Path<T>,
  mapping: LanguageMappingInterface<T>,
  chosenLanguage: LANGUAGE_KEYS,
): string {
  if (formKey.includes(".")) {
    const errorMappingReduction = formKey.split(".").reduce(
      (acc, formKeyPortion) => {
        // if we have reduced to nothing, skip
        if (acc.obj === null) return acc;

        // if we have come across an index that is a number, move on
        if (!isNaN(Number(formKeyPortion))) return acc;

        // otherwise, let's pull off the next field from our mapping
        const resultingObject = acc.obj[formKeyPortion];

        // if we don't have a resulting object, move on
        if (!resultingObject) return acc;

        // if the resulting object we have come across is indeed our LanguageObjectType,
        // then GREAT! We found our lowest level mapping and can return the correct language
        if (objectIsLanguageObjectType(resultingObject)) {
          acc = { label: (resultingObject as LanguageObjectType)[chosenLanguage], obj: null };
        } else {
          acc = { label: "", obj: resultingObject };
        }
        return acc;
      },
      // eslint-disable-next-line
      { label: "", obj: mapping as { [key: string]: any } | null },
    );

    return errorMappingReduction.label || "<LABEL MISSING>";
  }
  return "<LABEL MISSING>";
}

/**
 * This function does a lot, its fancy, and it is integral to making our internationalization pattern
 * for form labels work without a hitch
 *
 * It takes an incoming formKey form React Hook Form (which is typed as per our mutation parameters)
 * and it starts reducing into our language mapping by stepping into each of the sections of the formKey
 * and using them as keys to walk down into our mapping file.
 *
 * Once we have reached the end, we set the error message accordingly based on our language passed in
 * @param formKey (ie: generalDetails.birthDay, guardianDetails.guardians.0.firstName, livingConditions.individualsInHouse.2.relationship)
 * @param mapping
 * @param chosenLanguage
 * @returns
 */
export function getErrorFromFormKey<T extends FieldValues>(
  formKey: Path<T>,
  formErrors: FieldErrors<T>,
  customErrorMessage?: string,
): string {
  if (formKey.includes(".")) {
    const errorMappingReduction = formKey.split(".").reduce(
      (acc, formKeyPortion) => {
        // if we have reduced to nothing, skip
        if (acc.obj === null) return acc;

        const resultingObject = acc.obj[formKeyPortion];

        // if we don't have a resulting object, move on
        if (!resultingObject) return acc;

        // if the resulting object we have come across is indeed our LanguageObjectType,
        // then GREAT! We found our lowest level mapping and can return the correct language
        if (objectIsErrorObjectType(resultingObject)) {
          acc = {
            errorMessage: customErrorMessage
              ? customErrorMessage
              : (resultingObject as FieldErrors<T>[Path<T>])?.message?.toString() || "",
            obj: null,
          };
        } else {
          acc = { errorMessage: "", obj: resultingObject };
        }
        return acc;
      },
      // eslint-disable-next-line
      { errorMessage: "", obj: formErrors as { [key: string]: any } | null },
    );

    return errorMappingReduction.errorMessage;
  } else {
    // Handle the case where form key is not nested
    const error = formErrors[formKey as string];
    if (error && typeof error === "object" && "message" in error) {
      return error.message?.toString() || "";
    }
  }
  return customErrorMessage || "";
}

export interface QuestionData {
  questionKey: string;
  answer: string;
}

export const convertOnboardingQuestionListToQuestionMap = (
  data: QuestionData[] | null | undefined,
): { [key: string]: string } => {
  return (data ?? []).reduce(
    (acc, question) => {
      if (question && question.questionKey && question.answer) {
        acc[question.questionKey] = question.answer;
      }
      return acc;
    },
    {} as { [key: string]: string },
  );
};

type CombinedAnswers = {
  [key: string]: string;
};

type SeparatedAnswers = {
  [key: string]: string[] | string;
};

export const commaSeparatedDataToQuestionsmap = (combinedAnswers: CombinedAnswers): SeparatedAnswers => {
  const separatedAnswers: SeparatedAnswers = {};

  for (const key in combinedAnswers) {
    if (combinedAnswers.hasOwnProperty(key)) {
      // Check if the value contains a comma
      if (combinedAnswers[key].includes(",")) {
        // Split the combined answers by comma and trim any whitespace
        const answersArray = combinedAnswers[key].split(",").map((answer) => answer.trim());
        // Assign the array of separated answers to the key
        separatedAnswers[key] = answersArray;
      } else {
        // If no comma is present, keep the value as it is
        separatedAnswers[key] = combinedAnswers[key];
      }
    }
  }

  return separatedAnswers;
};
export const convertReadableString = (str: string | undefined | null): string => {
  if (!str) return "";
  // Split the input string on underscores
  const temp = str.split("_");

  // Map over the array to capitalize each item and join them back into a string
  const join = temp
    ?.map((item) => {
      return item.charAt(0).toUpperCase() + item.slice(1).toLowerCase();
    })
    ?.join(" ");
  return join;
};

export const convertDBString = (str: string): string => {
  // Split the input string into an array of words
  const words = str.split(" ");

  // Convert each word to uppercase and join them with underscores
  const joined = words.map((word) => word.toUpperCase()).join("_");
  return joined;
};

const applyTransformationByPath = <T, U>(object: T, path: string[], transform: (value: U) => U): T => {
  let current: any | undefined = object; // eslint-disable-line @typescript-eslint/no-explicit-any

  for (let i = 0; i < path.length - 1; i++) {
    if (current === null || current === undefined) {
      return object; // Path broken, exit or throw error as needed
    }

    current = current[path[i]];
  }
  const lastKey = path[path.length - 1];
  if (current && lastKey in current) {
    current[lastKey] = transform(current[lastKey]);
  }
  return object;
};

export const transformQueryData = <T, U>(queryData: T, transformations: Record<string, (value: U) => U>): T => {
  Object.keys(transformations).forEach((pathString) => {
    const path = pathString.split(".");
    applyTransformationByPath(queryData, path, transformations[pathString]);
  });
  return queryData;
};
